import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { PanelPlacementType, SizeChartResultType } from 'src/types/decorationSpecs';
import { changeEditorSkuSuccess, clearEditorSku } from 'src/pages/editor/components/surfaces/slice';

export type DecorationSpecsSliceState = {
    /** Identity of the product/selection the current data (or in-flight fetch) applies to —
     * `${sku}-${skuVersion}-${merchantSku}-${merchantVersion}-${JSON.stringify(merchantVariables)}`.
     * Used to skip redundant refetches and to ignore stale responses from a superseded request.
     * Covers `decorationPlacement`/`printWhite` (DIS) only — `sizeChart` is set independently via
     * `sizeChartResolved`; see that action's own docstring for why it isn't part of this
     * cache-key/loading scheme. */
    cacheKey: string | undefined;
    isLoading: boolean;
    /** `null` means DIS confirmed there's no data for this product (e.g. non-apparel) — distinct
     * from not having fetched yet (`cacheKey === undefined`). */
    decorationPlacement: PanelPlacementType[] | null;
    printWhite: boolean | null;
    /** `null` means either no size chart data is available for this product (e.g. non-apparel, or
     * a product whose measurements aren't modeled the way `productPropertySizeChart.ts` expects)
     * or nothing has resolved yet. Set via `sizeChartResolved`, not the DIS fetch this slice
     * otherwise tracks. */
    sizeChart: SizeChartResultType | null;
    /** Product options (e.g. `Backside`, `Decoration Technology`) still unset that are needed
     * before `decorationPlacement`/`printWhite` can be trusted — populated instead of those fields
     * when this is why they're absent/unreliable, so the UI can prompt for a selection rather than
     * showing "not available" or a possibly-wrong single answer. Computed proactively from the
     * product's own declared options (see `unresolvedProductOptions.ts`), not solely from
     * whatever DIS itself reports, since DIS's own "needs more info" signaling is inconsistent. */
    decorationMissingSelections: string[] | null;
    error?: string;
};

export const initialState: DecorationSpecsSliceState = {
    cacheKey: undefined,
    isLoading: false,
    decorationPlacement: null,
    printWhite: null,
    sizeChart: null,
    decorationMissingSelections: null,
    error: undefined,
};

type RetrieveDecorationSpecsPayload = { cacheKey: string };

type RetrieveDecorationSpecsSettledPayload = {
    cacheKey: string;
    decorationPlacement: PanelPlacementType[] | null;
    printWhite: boolean | null;
    decorationMissingSelections: string[] | null;
    error?: string;
};

const slice = createSlice({
    name: 'decorationSpecs',
    initialState,
    reducers: {
        retrieveDecorationSpecs: (state, { payload }: PayloadAction<RetrieveDecorationSpecsPayload>) => {
            state.cacheKey = payload.cacheKey;
            state.isLoading = true;
            state.error = undefined;
            // Clear the previous DIS result immediately, not just when the new one settles. The
            // previous result describes the *old* selection — if a designer removes or changes an
            // option that made it valid (e.g. clearing `Backside` after DIS had already answered
            // for a fully-specified selection), that answer is no longer trustworthy and must not
            // keep rendering as if it still applies for however long this fetch takes.
            //
            // Deliberately does NOT clear `sizeChart` — it's resolved independently (see
            // `sizeChartResolved`) and stays on screen across a DIS refetch rather than
            // disappearing and reappearing on every selection change; `DecorationSpecs`'s own loading
            // state only ever covers the decoration-placement/white-ink sections. A stale size
            // chart can briefly show mid-product-switch until `VariablesV3` resolves the new one
            // and overwrites it — accepted as a better trade-off than the visible flicker.
            state.decorationPlacement = null;
            state.printWhite = null;
            state.decorationMissingSelections = null;
        },
        retrieveDecorationSpecsSettled: (state, { payload }: PayloadAction<RetrieveDecorationSpecsSettledPayload>) => {
            // Guard against a slower, now-superseded request resolving after a newer selection
            // change already started a different fetch.
            if (state.cacheKey !== payload.cacheKey) {
                return;
            }
            state.isLoading = false;
            state.decorationPlacement = payload.decorationPlacement;
            state.printWhite = payload.printWhite;
            state.decorationMissingSelections = payload.decorationMissingSelections;
            state.error = payload.error;
        },
        /**
         * Sets the size chart resolved locally from `GenericSelector`'s own product data (see
         * `productPropertySizeChart.ts`) — dispatched from `VariablesV3` whenever its selection
         * resolves, not from this slice's own saga/cache-key scheme. Unlike
         * `retrieveDecorationSpecsSettled`, there's no cache-key guard here: this is a synchronous
         * derivation from the *current* render of the SKU picker (not a network response that
         * could arrive late and out of order), so whatever's dispatched is by construction already
         * the freshest answer for whatever's currently selected.
         */
        sizeChartResolved: (state, { payload }: PayloadAction<SizeChartResultType | null>) => {
            state.sizeChart = payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(clearEditorSku, () => initialState).addCase(changeEditorSkuSuccess, () => initialState);
    },
});

export const reducer = slice.reducer;
export const { retrieveDecorationSpecs, retrieveDecorationSpecsSettled, sizeChartResolved } = slice.actions;
